// Accumulation Calculator - Mobile Wizard Interface
// Complete mobile-first experience for accumulation phase planning

import React, { useState, useRef } from 'react';
import { SimulationEngine } from '../SimulationEngine';
import { FinancialConfig } from '../FinancialConfig';
import { PDFExportManager } from '../PDFExportManager';
import CalculatorBridge from '../CalculatorBridge';
import MobileLoadingSpinner from './MobileLoadingSpinner';
import { EmailCaptureForm } from './EmailCaptureForm';
import { SubscriptionManager } from '../SubscriptionManager';
import { openPricingModal } from '../utils/openPricingModal.js';
import { alertCheckoutFailure, startRecommendedCheckout } from '../utils/recommendedCheckout.js';
import CalculationTransparency from './CalculationTransparency.js';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip } from 'recharts';

/** Map engine result fields to mobile display shape (matches desktop AccumulationOriginal). */
function mapAccumulationResultsForDisplay(engineResult, inputs) {
    if (!engineResult || engineResult.error) return engineResult;

    const fb = engineResult.finalBalances || {};
    const totalFinalBalance = fb.total ?? fb.median ?? fb.combinedWealth ?? 0;

    const yearsToRetirement = Math.max(
        (parseInt(inputs.retirementAge, 10) || 67) - (parseInt(inputs.currentAge, 10) || 0),
        0
    );
    const avgAnnualContributions = yearsToRetirement > 0 && engineResult.totalContributions
        ? Math.round(engineResult.totalContributions / yearsToRetirement)
        : null;

    let chartData = null;
    const yearlyData = engineResult.yearlyData || engineResult.combinedData;
    if (yearlyData && yearlyData.length > 0) {
        const isCouple = inputs.accumulationRelationshipStatus === 'couple';
        chartData = yearlyData.map((row, index) => {
            const year = row.year ?? index;
            const userAge = Number(inputs.currentAge) + index;
            const partnerAge = isCouple ? Number(inputs.partnerCurrentAge) + index : null;
            const userBal = Number(row.userBalance) || 0;
            const partnerBal = Number(row.partnerBalance) || 0;
            const truncatedUserBal = userAge > Number(inputs.retirementAge) ? null : Math.round(userBal);
            const truncatedPartnerBal = (isCouple && partnerAge > Number(inputs.partnerRetirementAge))
                ? null
                : Math.round(partnerBal);
            const totalBal = (truncatedUserBal !== null ? truncatedUserBal : 0)
                + (truncatedPartnerBal !== null ? truncatedPartnerBal : 0);
            return {
                year,
                userAge,
                partnerAge,
                userBalance: truncatedUserBal,
                partnerBalance: truncatedPartnerBal,
                totalBalance: Math.round(totalBal)
            };
        });
    }

    return {
        ...engineResult,
        totalFinalBalance,
        chartData,
        avgAnnualContributions
    };
}

const DEFAULT_ACCUMULATION_MOBILE_INPUTS = {
    accumulationRelationshipStatus: 'single',
    currentAge: '',
    retirementAge: '',
    currentSuper: '',
    currentWage: '',
    wageGrowthRate: 0.3,
    additionalContributions: '',
    nonConcessionalContributions: '',
    enableBringForward: false,
    unusedConcessionalContributions: '',
    downsizerContribution: '',
    downsizerYear: '',
    spouseContribution: '',
    enableCoContribution: true,
    enableLISTO: true,
    listoAdjustedTaxableIncome: '',
    partnerListoAdjustedTaxableIncome: '',
    enableTTR: false,
    ttrStartAge: '',
    ttrDrawdownRate: '',
    accumulationPhasePercent: '',
    enableWorkTest: true,
    portfolioTurnover: '',
    dividendPortion: '',
    partnerCurrentAge: '',
    partnerRetirementAge: '',
    partnerCurrentSuper: '',
    partnerCurrentWage: '',
    partnerAdditionalContributions: '',
    partnerNonConcessionalContributions: '',
    partnerEnableBringForward: false,
    partnerUnusedConcessionalContributions: '',
    partnerDownsizerContribution: '',
    partnerDownsizerYear: '',
    partnerSpouseContribution: '',
    partnerEnableTTR: false,
    partnerTtrStartAge: '',
    partnerTtrDrawdownRate: '',
    partnerAccumulationPhasePercent: '',
    partnerEnableWorkTest: true,
    accumulationMode: 'assumed',
    assumedReturn: 7.5,
    assumedInflation: 2.5,
    sp500: 35,
    ausShares: 15,
    intlShares: 20,
    bonds: 20,
    cash: 10,
    frankingCredits: 80,
    adminFees: 52,
    adminFeePercentage: 0.1,
    investmentFees: 0.5,
    insuranceFees: 0
};

export const AccumulationCalculatorMobile = ({ onBack }) => {
    
    const [renderError, setRenderError] = useState(null);
    const [expandedSection, setExpandedSection] = useState(0);
    const [hasCalculated, setHasCalculated] = useState(false);
    
    // Error boundary
    React.useEffect(() => {
        const errorHandler = (error) => {
            console.error('❌ Runtime error in Accumulation:', error);
            setRenderError(error.message);
        };
        window.addEventListener('error', errorHandler);
        return () => window.removeEventListener('error', errorHandler);
    }, []);
    
    // Show error if component crashes
    if (renderError) {
        return (
            <div className="min-h-screen bg-red-50 p-4">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Calculator</h2>
                    <p className="text-gray-700 mb-4">{renderError}</p>
                    <button onClick={onBack} className="px-4 py-2 bg-blue-600 text-white rounded">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }
    
    // Load saved inputs from localStorage
    const getInitialInputs = () => {
        // Check for transfer data FIRST (from SMSF or other calculators)
        const transferData = CalculatorBridge.retrieveTransferData('accumulation');
        if (transferData) {
            return transferData;
        }
        
        // Then check saved inputs (Pro only: save/restore is gated)
        if (SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) {
            try {
                const saved = localStorage.getItem('accumulationCalculatorMobile_inputs');
                if (saved) {
                    return JSON.parse(saved);
                }
            } catch (error) {
                console.error('Error loading saved inputs:', error);
            }
        }
        
        return { ...DEFAULT_ACCUMULATION_MOBILE_INPUTS };
    };
    
    const [inputs, setInputs] = useState(getInitialInputs());
    const [results, setResults] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [showDetailedChart, setShowDetailedChart] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [showInputsRevertModal, setShowInputsRevertModal] = useState(false);
    const resultsRef = useRef(null);
    const lastRunInputsRef = useRef(null);
    const getInputsForResults = () => lastRunInputsRef.current ?? inputs;
    
    // Save inputs to localStorage (Pro only: save/restore is gated)
    React.useEffect(() => {
        if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) return;
        try {
            localStorage.setItem('accumulationCalculatorMobile_inputs', JSON.stringify(inputs));
        } catch (error) {
            console.error('Error saving inputs:', error);
        }
    }, [inputs]);
    
    // Hide header on scroll
    React.useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.pageYOffset > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    
    const handleInputChange = (key, value) => {
        // If changing to single, clear all partner fields
        if (key === 'accumulationRelationshipStatus' && value === 'single') {
            setInputs(prev => ({
                ...prev,
                [key]: value,
                partnerCurrentAge: '',
                partnerRetirementAge: '',
                partnerCurrentSuper: '',
                partnerCurrentWage: '',
                partnerAdditionalContributions: '',
                partnerNonConcessionalContributions: '',
                partnerEnableBringForward: false,
                partnerUnusedConcessionalContributions: '',
                partnerDownsizerContribution: '',
                partnerDownsizerYear: '',
                partnerSpouseContribution: '',
                partnerEnableTTR: false,
                partnerTtrStartAge: '',
                partnerTtrDrawdownRate: '',
                partnerAccumulationPhasePercent: '',
                partnerEnableWorkTest: true
            }));
        } else {
            setInputs(prev => ({ ...prev, [key]: value }));
        }
        if (hasCalculated) {
            setHasCalculated(false);
            setResults(null);
        }
    };
    
    const toggleSection = (index) => {
        const newExpanded = expandedSection === index ? -1 : index;
        setExpandedSection(newExpanded);
        
        // Scroll to the section that was just opened
        if (newExpanded !== -1) {
            setTimeout(() => {
                const sectionElement = document.getElementById(`accumulation-section-${index}`);
                if (sectionElement) {
                    const headerOffset = 60;
                    const elementPosition = sectionElement.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 150);
        }
    };
    
    const handleCalculate = async () => {
        try {
            setIsCalculating(true);
            
            // Wait for React to render the spinner (double RAF ensures paint)
            await new Promise(resolve => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setTimeout(resolve, 100);
                    });
                });
            });
            
            const isCouple = inputs.accumulationRelationshipStatus === 'couple';
            const currentAge = parseInt(inputs.currentAge || 0);
            const retirementAge = parseInt(inputs.retirementAge || 67);
            const yearsToRetirement = retirementAge - currentAge;
            
            if (yearsToRetirement <= 0) {
                setIsCalculating(false);
                alert('Retirement age must be greater than current age');
                return;
            }
            
            // Collect one-time contributions (downsizer)
            const oneTimeContributions = {
                user: {
                    concessional: [],
                    nonConcessional: []
                },
                partner: {
                    concessional: [],
                    nonConcessional: []
                }
            };
            
            // Add downsizer as one-time non-concessional
            if (inputs.downsizerContribution && inputs.downsizerYear) {
                oneTimeContributions.user.nonConcessional.push({
                    year: parseInt(inputs.downsizerYear),
                    amount: parseFloat(inputs.downsizerContribution)
                });
            }
            
            if (inputs.partnerDownsizerContribution && inputs.partnerDownsizerYear) {
                oneTimeContributions.partner.nonConcessional.push({
                    year: parseInt(inputs.partnerDownsizerYear),
                    amount: parseFloat(inputs.partnerDownsizerContribution)
                });
            }
            
            // Call EXACT same function as desktop
            const result = SimulationEngine.runAccumulationSimulation(inputs, oneTimeContributions);
            
            setResults(mapAccumulationResultsForDisplay(result, inputs));
            setHasCalculated(true);
            lastRunInputsRef.current = { ...inputs };
            // Free users: keep inputs on this visit; no localStorage restore. Soft note instead of wipe.
            if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && !result.error) {
                setShowInputsRevertModal(true);
            }
            setIsCalculating(false);
            
            setTimeout(() => {
                resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
            
        } catch (error) {
            console.error('Calculation error:', error);
            setIsCalculating(false);
            alert(`Error: ${error.message}. Please check all inputs.`);
        }
    };
    
    // FULL SECTION DEFINITIONS - ALL ACCUMULATION FIELDS
    const sections = [
        {
            id: 0,
            title: 'Personal Details',
            icon: '👤',
            subtitle: `${inputs.accumulationRelationshipStatus === 'couple' ? 'Couple' : 'Single'}, Age ${inputs.currentAge || '?'}`,
            fields: [
                {
                    key: 'accumulationRelationshipStatus',
                    label: 'Relationship Status',
                    type: 'select',
                    options: [
                        { value: 'single', label: 'Single' },
                        { value: 'couple', label: 'Couple' }
                    ]
                },
                {
                    key: 'currentAge',
                    label: 'Your Current Age',
                    type: 'number',
                    min: 18,
                    max: 120,
                    helper: 'Your age today'
                },
                {
                    key: 'retirementAge',
                    label: 'Planned Retirement Age',
                    type: 'number',
                    min: 55,
                    max: 75,
                    helper: 'When you plan to retire'
                },
                {
                    key: 'partnerCurrentAge',
                    label: "Partner's Current Age",
                    type: 'number',
                    min: 18,
                    max: 120,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                },
                {
                    key: 'partnerRetirementAge',
                    label: "Partner's Retirement Age",
                    type: 'number',
                    min: 55,
                    max: 75,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                }
            ]
        },
        {
            id: 1,
            title: 'Current Situation',
            icon: '💰',
            subtitle: `Super: $${(parseFloat(inputs.currentSuper || 0) + (inputs.accumulationRelationshipStatus === 'couple' ? parseFloat(inputs.partnerCurrentSuper || 0) : 0)).toLocaleString()}`,
            fields: [
                {
                    key: 'currentSuper',
                    label: 'Your Current Super Balance ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Total superannuation balance today'
                },
                {
                    key: 'currentWage',
                    label: 'Your Current Wage ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Annual salary before tax'
                },
                {
                    key: 'partnerCurrentSuper',
                    label: "Partner's Current Super ($)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                },
                {
                    key: 'partnerCurrentWage',
                    label: "Partner's Current Wage ($/year)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                }
            ]
        },
        {
            id: 2,
            title: 'Regular Contributions',
            icon: '📈',
            subtitle: `+$${(parseFloat(inputs.additionalContributions || 0) + parseFloat(inputs.nonConcessionalContributions || 0)).toLocaleString()}/yr`,
            fields: [
                {
                    key: 'additionalContributions',
                    label: 'Your Voluntary Concessional ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Pre-tax salary sacrifice contributions'
                },
                {
                    key: 'nonConcessionalContributions',
                    label: 'Your After-Tax Contributions ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Post-tax contributions (non-concessional)'
                },
                {
                    key: 'enableBringForward',
                    label: 'Use Bring-Forward Rule',
                    type: 'checkbox',
                    helper: 'Access 3 years of non-concessional caps'
                },
                {
                    key: 'unusedConcessionalContributions',
                    label: 'Unused Concessional Cap ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Carry-forward unused caps from previous years'
                },
                {
                    key: 'spouseContribution',
                    label: 'Spouse Contribution ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Contributions made on behalf of spouse'
                },
                {
                    key: 'enableCoContribution',
                    label: 'Enable Government Co-Contribution',
                    type: 'checkbox',
                    helper: 'Automatic co-contribution if eligible'
                },
                {
                    key: 'enableLISTO',
                    label: 'Include LISTO (low income super tax offset)',
                    type: 'checkbox',
                    helper: 'Refunds up to $500 of contributions tax when eligible. Uses wage as adjusted taxable income unless you enter ATI below.'
                },
                {
                    key: 'listoAdjustedTaxableIncome',
                    label: 'Your adjusted taxable income for LISTO ($/year, optional)',
                    type: 'currency',
                    min: 0,
                    helper: 'Leave blank to use wage/salary as a simplified proxy'
                },
                {
                    key: 'wageGrowthRate',
                    label: 'Real Wage Growth Rate (%/year)',
                    type: 'percentage',
                    min: -2,
                    max: 5,
                    step: 0.1,
                    helper: 'Historical Australian average: 0.3-0.4%. ASIC guidance: 1.2% (optimistic)'
                },
                {
                    key: 'partnerAdditionalContributions',
                    label: "Partner's Voluntary Concessional ($/year)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                },
                {
                    key: 'partnerNonConcessionalContributions',
                    label: "Partner's After-Tax Contributions ($/year)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                },
                {
                    key: 'partnerEnableBringForward',
                    label: "Partner: Use Bring-Forward Rule",
                    type: 'checkbox',
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                },
                {
                    key: 'partnerUnusedConcessionalContributions',
                    label: "Partner's Unused Concessional Cap ($)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                },
                {
                    key: 'partnerSpouseContribution',
                    label: "Partner's Spouse Contribution ($/year)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                },
                {
                    key: 'partnerListoAdjustedTaxableIncome',
                    label: "Partner's adjusted taxable income for LISTO ($/year, optional)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple',
                    helper: 'Leave blank to use partner wage as a simplified proxy'
                }
            ]
        },
        {
            id: 3,
            title: 'One-Time Contributions',
            icon: '💎',
            subtitle: `Downsizer: $${(parseFloat(inputs.downsizerContribution || 0)).toLocaleString()}`,
            fields: [
                {
                    key: 'downsizerContribution',
                    label: 'Downsizer Contribution ($)',
                    type: 'currency',
                    min: 0,
                    max: 300000,
                    helper: 'Up to $300k from home sale (age 55+)'
                },
                {
                    key: 'downsizerYear',
                    label: 'Downsizer Year',
                    type: 'number',
                    min: 0,
                    max: 50,
                    helper: 'Years from now when downsizer contribution made'
                },
                {
                    key: 'partnerDownsizerContribution',
                    label: "Partner's Downsizer Contribution ($)",
                    type: 'currency',
                    min: 0,
                    max: 300000,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                },
                {
                    key: 'partnerDownsizerYear',
                    label: "Partner's Downsizer Year",
                    type: 'number',
                    min: 0,
                    max: 50,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                }
            ]
        },
        {
            id: 4,
            title: 'TTR & Drawdown',
            icon: '💳',
            subtitle: inputs.enableTTR ? `TTR at ${inputs.ttrStartAge || 60}` : 'TTR Disabled',
            fields: [
                {
                    key: 'enableTTR',
                    label: 'Enable Transition to Retirement (TTR)',
                    type: 'checkbox',
                    helper: 'Draw pension while still working'
                },
                {
                    key: 'ttrStartAge',
                    label: 'TTR Start Age',
                    type: 'number',
                    min: 60,
                    max: 75,
                    helper: 'When to start TTR pension',
                    showIf: () => inputs.enableTTR
                },
                {
                    key: 'ttrDrawdownRate',
                    label: 'TTR Drawdown Rate (%)',
                    type: 'percentage',
                    min: 4,
                    max: 10,
                    step: 0.5,
                    helper: 'Percentage to draw annually (4-10%)',
                    showIf: () => inputs.enableTTR
                },
                {
                    key: 'accumulationPhasePercent',
                    label: 'Accumulation Phase Allocation (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    helper: 'Percentage remaining in accumulation',
                    showIf: () => inputs.enableTTR
                },
                {
                    key: 'enableWorkTest',
                    label: 'Subject to Work Test',
                    type: 'checkbox',
                    helper: 'Age 67-74 must meet work test'
                },
                {
                    key: 'partnerEnableTTR',
                    label: "Partner: Enable TTR",
                    type: 'checkbox',
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                },
                {
                    key: 'partnerTtrStartAge',
                    label: "Partner's TTR Start Age",
                    type: 'number',
                    min: 60,
                    max: 75,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple' && inputs.partnerEnableTTR
                },
                {
                    key: 'partnerTtrDrawdownRate',
                    label: "Partner's TTR Drawdown Rate (%)",
                    type: 'percentage',
                    min: 4,
                    max: 10,
                    step: 0.5,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple' && inputs.partnerEnableTTR
                },
                {
                    key: 'partnerAccumulationPhasePercent',
                    label: "Partner's Accumulation Phase (%)",
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple' && inputs.partnerEnableTTR
                },
                {
                    key: 'partnerEnableWorkTest',
                    label: "Partner: Subject to Work Test",
                    type: 'checkbox',
                    showIf: () => inputs.accumulationRelationshipStatus === 'couple'
                }
            ]
        },
        {
            id: 5,
            title: 'Portfolio Allocation',
            icon: '📊',
            subtitle: inputs.accumulationMode === 'assumed' ? `${inputs.assumedReturn}% return` : 'Historical data',
            fields: [
                {
                    key: 'accumulationMode',
                    label: 'Calculation Mode',
                    type: 'select',
                    options: [
                        { value: 'assumed', label: 'Assumed Growth' },
                        { value: 'historical', label: 'Historical Monte Carlo' }
                    ]
                },
                {
                    key: 'assumedReturn',
                    label: 'Expected Return (%/year)',
                    type: 'percentage',
                    min: 0,
                    max: 20,
                    step: 0.5,
                    helper: 'Expected investment return',
                    showIf: () => inputs.accumulationMode === 'assumed'
                },
                {
                    key: 'assumedInflation',
                    label: 'Expected Inflation (%/year)',
                    type: 'percentage',
                    min: 0,
                    max: 10,
                    step: 0.5,
                    helper: 'RBA target: 2-3%',
                    showIf: () => inputs.accumulationMode === 'assumed'
                },
                {
                    key: 'sp500',
                    label: 'S&P 500 (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    showIf: () => inputs.accumulationMode === 'historical'
                },
                {
                    key: 'ausShares',
                    label: 'Australian Shares (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    showIf: () => inputs.accumulationMode === 'historical'
                },
                {
                    key: 'intlShares',
                    label: 'International Shares (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    showIf: () => inputs.accumulationMode === 'historical'
                },
                {
                    key: 'bonds',
                    label: 'Bonds (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    showIf: () => inputs.accumulationMode === 'historical'
                },
                {
                    key: 'cash',
                    label: 'Cash (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    showIf: () => inputs.accumulationMode === 'historical'
                },
                {
                    key: 'frankingCredits',
                    label: 'Franking Credits (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    helper: 'Percentage of dividends franked',
                    showIf: () => inputs.accumulationMode === 'historical'
                },
                {
                    key: 'portfolioTurnover',
                    label: 'Portfolio Turnover (%/year)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    helper: 'Annual portfolio turnover rate'
                },
                {
                    key: 'dividendPortion',
                    label: 'Dividend Portion (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    helper: 'Percentage of returns from dividends'
                }
            ]
        },
        {
            id: 6,
            title: 'Fees',
            icon: '💳',
            subtitle: `$${parseFloat(inputs.adminFees || 0) + parseFloat(inputs.insuranceFees || 0)}/yr + ${(parseFloat(inputs.adminFeePercentage || 0) + parseFloat(inputs.investmentFees || 0)).toFixed(2)}%`,
            fields: [
                {
                    key: 'adminFees',
                    label: 'Fixed Admin Fee ($/year)',
                    type: 'currency',
                    min: 0
                },
                {
                    key: 'adminFeePercentage',
                    label: 'Admin Fee (%)',
                    type: 'percentage',
                    min: 0,
                    max: 5,
                    step: 0.01
                },
                {
                    key: 'investmentFees',
                    label: 'Investment Fee (%)',
                    type: 'percentage',
                    min: 0,
                    max: 5,
                    step: 0.01
                },
                {
                    key: 'insuranceFees',
                    label: 'Insurance Premium ($/year)',
                    type: 'currency',
                    min: 0
                }
            ]
        }
    ];
    
    const renderField = (field) => {
        try {
            if (field.showIf && !field.showIf()) return null;
            
            const value = inputs[field.key];
        
        return (
            <div key={field.key} className="mb-5">
                <label className="block text-base font-bold text-gray-900 mb-2">
                    {field.label}
                </label>
                
                {field.type === 'select' && (
                    <select
                        value={value}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        className="w-full px-4 py-4 text-base font-medium border-2 border-gray-300 rounded-xl bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-200"
                        style={{ fontSize: '16px' }}
                    >
                        {field.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                )}
                
                {field.type === 'checkbox' && (
                    <label className="flex items-center gap-4 p-5 bg-gray-50 rounded-xl border-2 border-gray-200 active:bg-gray-100">
                        <input
                            type="checkbox"
                            checked={value || false}
                            onChange={(e) => handleInputChange(field.key, e.target.checked)}
                            className="w-7 h-7 text-green-600 rounded-md flex-shrink-0"
                        />
                        <span className="text-base font-medium text-gray-800 flex-1">
                            {field.helper || field.label}
                        </span>
                    </label>
                )}
                
                {(field.type === 'number' || field.type === 'currency' || field.type === 'percentage') && (
                    <div className="relative">
                        <input
                            type="number"
                            value={value === '' ? '' : value}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '') {
                                    handleInputChange(field.key, '');
                                } else {
                                    handleInputChange(field.key, parseFloat(val) || 0);
                                }
                            }}
                            placeholder={field.type === 'currency' ? '0' : field.type === 'percentage' ? '0.0' : '0'}
                            min={field.min}
                            max={field.max}
                            step={field.step || 1}
                            className="w-full px-5 py-5 text-xl font-bold text-right border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-200"
                            style={{ fontSize: '18px' }}
                        />
                        {field.type === 'currency' && (
                            <span className="absolute left-5 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-500">$</span>
                        )}
                        {field.type === 'percentage' && (
                            <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-500">%</span>
                        )}
                    </div>
                )}
                
                {field.helper && (
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{field.helper}</p>
                )}
            </div>
        );
        } catch (error) {
            console.error('Field render error:', field.key, error);
            return (
                <div key={field.key} className="mb-5 p-4 bg-red-50 rounded-xl">
                    <p className="text-sm text-red-600">Error rendering {field.label}</p>
                </div>
            );
        }
    };
    
    try {
        return (
            <div className="min-h-screen bg-gray-50" style={{ paddingBottom: hasCalculated ? 'max(calc(env(safe-area-inset-bottom) + 1.5rem), 2rem)' : 'max(calc(env(safe-area-inset-bottom) + 7.5rem), 8rem)' }}>
            {/* Loading Spinner - Calculations */}
            {isCalculating && (
                <MobileLoadingSpinner 
                    message="Calculating accumulation..."
                    subMessage="Running simulation..."
                />
            )}
            
            {/* Loading Spinner - PDF Export */}
            {isExporting && (
                <MobileLoadingSpinner 
                    message="Generating PDF..."
                    subMessage="Please wait while we create your report..."
                />
            )}
            
            {/* Header */}
            <div className={`bg-green-600 text-white px-3 sticky top-0 z-20 shadow-md transition-all ${
                isScrolled ? 'py-2' : 'py-3'
            }`}
                 style={{ paddingTop: isScrolled ? '8px' : 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-green-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className={`font-bold transition-all ${isScrolled ? 'text-sm' : 'text-base'}`}>
                        Accumulation
                    </h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            {!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && (
                <p className="px-3 py-1.5 text-xs text-gray-500 text-center">Free: edit and re-run anytime this visit. Subscribe to keep inputs after you leave.</p>
            )}
            
            {/* Accordion Sections */}
            <div className="p-3 space-y-2">
                {sections.map((section, index) => (
                    <div key={section.id} id={`accumulation-section-${index}`} className="bg-white rounded-xl shadow-sm overflow-hidden">
                        {/* Section Header */}
                        <button
                            onClick={() => toggleSection(index)}
                            className="w-full p-4 flex items-center justify-between active:bg-gray-50 transition"
                        >
                            <div className="flex items-center gap-3 flex-1">
                                <span className="text-2xl">{section.icon}</span>
                                <div className="text-left">
                                    <div className="font-bold text-gray-900">{section.title}</div>
                                    <div className="text-xs text-gray-600">{section.subtitle}</div>
                                </div>
                            </div>
                            <svg
                                className={`w-5 h-5 text-gray-400 transition-transform ${
                                    expandedSection === index ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        
                        {/* Section Content */}
                        {expandedSection === index && (
                            <div className="p-4 border-t border-gray-100">
                                <div className="space-y-4">
                                    {section.fields.map(field => renderField(field))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            
            {/* Results */}
            {hasCalculated && results && !results.error && (
                <div ref={resultsRef} className="p-4 space-y-4 mb-20">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-1 rounded-2xl shadow-xl">
                        <div className="bg-white rounded-xl p-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                                Your Retirement Projection
                            </h2>
                            
                            {/* Final Balance at Retirement */}
                            <div className="bg-green-50 p-6 rounded-xl border-2 border-green-200 mb-4">
                                <div className="text-sm font-semibold text-gray-600 mb-2">
                                    Super Balance at Retirement
                                </div>
                                <div className="text-4xl font-bold text-green-600 mb-1">
                                    ${Math.round(results.totalFinalBalance || 0).toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                    At age {getInputsForResults().retirementAge}
                                </div>
                            </div>
                            
                            {/* Annual Contributions */}
                            {results.avgAnnualContributions && (
                                <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200">
                                    <div className="text-sm font-semibold text-gray-600 mb-2">
                                        Average Annual Contributions
                                    </div>
                                    <div className="text-3xl font-bold text-blue-600 mb-1">
                                        ${Math.round(results.avgAnnualContributions).toLocaleString()}/year
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Soft persistence note — non-blocking */}
                    {showInputsRevertModal && (
                        <div className="rounded-xl border border-green-200 bg-green-50 p-4" role="status">
                            <div className="flex gap-3 items-start">
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-green-900 mb-1">Your numbers are still in the form above</p>
                                    <p className="text-xs text-green-800 mb-0">
                                        Edit any field and run again on this visit. They won&apos;t be remembered after you leave — subscribe to auto-save.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowInputsRevertModal(false)}
                                    className="text-green-700 text-lg leading-none px-1 flex-shrink-0"
                                    aria-label="Dismiss"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button
                                    type="button"
                                    onClick={() => setShowInputsRevertModal(false)}
                                    className="flex-1 py-2.5 px-3 bg-white text-green-900 font-semibold rounded-lg text-sm border border-green-200 min-h-[44px]"
                                >
                                    Got it
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowInputsRevertModal(false);
                                        openPricingModal({
                                            source: 'accumulation_mobile_soft_wipe',
                                            calculatorTier: 'accumulation',
                                            surface: 'accumulation_mobile_persistence_note',
                                        });
                                    }}
                                    className="flex-1 py-2.5 px-3 bg-green-600 text-white font-semibold rounded-lg text-sm min-h-[44px]"
                                >
                                    See pricing
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Email Capture - After Results */}
                    <EmailCaptureForm 
                        calculatorType="accumulation"
                        results={results}
                        inputs={getInputsForResults()}
                        getPdfBase64={async () => PDFExportManager.exportAccumulationCalculator(getInputsForResults(), results, {}, true)}
                    />

                    <CalculationTransparency
                        kind="accumulation"
                        inputs={getInputsForResults()}
                        results={results}
                    />
                    
                    {/* Action Buttons */}
                    <div className="space-y-3">
                        <button 
                            onClick={() => setShowDetailedChart(true)}
                            className="w-full py-4 bg-purple-600 text-white font-bold text-base rounded-xl shadow-lg active:bg-purple-700"
                        >
                            📊 View Detailed Chart
                        </button>
                        <button 
                            onClick={async () => {
                                if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                                    startRecommendedCheckout({
                                        tier: 'essential',
                                        billing: 'monthly',
                                        source: 'pdf_export',
                                        calculatorTier: 'accumulation',
                                        surface: 'accumulation_mobile_pdf_export',
                                    }).catch(alertCheckoutFailure);
                                    return;
                                }
                                try {
                                    setIsExporting(true);
                                    await new Promise(resolve => {
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                setTimeout(resolve, 100);
                                            });
                                        });
                                    });
                                    
                                    // EXACT same params as desktop — use snap so free wipe does not corrupt export
                                    await PDFExportManager.exportAccumulationCalculator(getInputsForResults(), results, {});
                                    
                                    setIsExporting(false);
                                } catch (error) {
                                    console.error('PDF export error:', error);
                                    setIsExporting(false);
                                    alert('Error generating PDF. Please try again.');
                                }
                            }}
                            className="w-full py-4 bg-gray-600 text-white font-bold text-base rounded-xl shadow-lg active:bg-gray-700"
                        >
                            📄 Export PDF
                        </button>
                        <button 
                            onClick={() => {
                                setHasCalculated(false);
                                setResults(null);
                                lastRunInputsRef.current = null;
                                setShowInputsRevertModal(false);
                                setExpandedSection(0);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="w-full py-4 bg-white text-gray-700 font-bold text-base rounded-xl border-2 border-gray-300"
                        >
                            🔄 New Calculation
                        </button>
                    </div>
                </div>
            )}
            
            {/* Detailed Chart Modal */}
            {showDetailedChart && results && (
                <div className="fixed inset-0 bg-black bg-opacity-70 z-50 overflow-auto" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
                    <div className="min-h-screen p-4">
                        <div className="bg-white rounded-xl w-full max-w-4xl mx-auto mb-4">
                            {/* Modal Header */}
                            <div className="bg-green-600 text-white p-4 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
                                <h3 className="text-lg font-bold">Accumulation Projection</h3>
                                <button 
                                    onClick={() => setShowDetailedChart(false)}
                                    className="text-white bg-green-700 hover:bg-green-800 p-2 rounded-lg w-10 h-10 flex items-center justify-center"
                                >
                                    ✕
                                </button>
                            </div>
                            
                            {/* Modal Content */}
                            <div className="p-4 space-y-6">
                                {/* Growth Chart */}
                                {results.chartData && (
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-3">Super Growth Over Time</h3>
                                        <ResponsiveContainer width="100%" height={250}>
                                            <AreaChart data={results.chartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
                                                <YAxis tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                                                <Tooltip formatter={(val) => `$${val.toLocaleString()}`} />
                                                <Legend />
                                                <Area 
                                                    type="monotone" 
                                                    dataKey="totalBalance" 
                                                    name="Total Balance"
                                                    stroke="#10B981" 
                                                    fill="#10B981" 
                                                    fillOpacity={0.3}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>
                            
                            {/* Modal Footer */}
                            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
                                <button 
                                    onClick={() => setShowDetailedChart(false)}
                                    className="w-full py-3 bg-green-600 text-white font-bold rounded-lg active:bg-green-700"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Calculate Button */}
            {!hasCalculated && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-30 px-3 py-2"
                     style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}>
                    <button
                        onClick={handleCalculate}
                        className="w-full py-4 bg-green-600 text-white font-bold text-base rounded-xl active:bg-green-700 transition flex items-center justify-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Calculate
                        </button>
                    </div>
                )}
            </div>
        );
    } catch (error) {
        console.error('❌ Accumulation render crashed:', error);
        return (
            <div className="min-h-screen bg-red-50 p-4">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h2 className="text-xl font-bold text-red-600 mb-4">Calculator Error</h2>
                    <p className="text-gray-700 mb-2">The Accumulation calculator encountered an error:</p>
                    <p className="text-sm text-red-600 font-mono bg-red-50 p-3 rounded mb-4">{error.toString()}</p>
                    <button 
                        onClick={onBack}
                        className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl"
                    >
                        ← Back to Calculators
                    </button>
                </div>
            </div>
        );
    }
};

export default AccumulationCalculatorMobile;

